import { alpha, Box, Chip, IconButton, styled } from '@mui/material'
import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { KeyboardEvent, MouseEvent } from 'react'
import { announce } from '@app/components/common/ScreenReaderAnnouncer'
import { TransformComponent } from 'react-zoom-pan-pinch'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlassPlus } from '@fortawesome/pro-solid-svg-icons/faMagnifyingGlassPlus'
import { faMagnifyingGlassMinus } from '@fortawesome/pro-solid-svg-icons/faMagnifyingGlassMinus'
import { faMaximize } from '@fortawesome/pro-solid-svg-icons/faMaximize'
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark'
import { faArrowUp } from '@fortawesome/pro-solid-svg-icons/faArrowUp'
import { faArrowDown } from '@fortawesome/pro-solid-svg-icons/faArrowDown'
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft'
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight'
import { focusStyle } from '@app/theme'

type PanDirection = 'up' | 'down' | 'left' | 'right'

type ZoomableImageContentProps = {
  imageScale: number
  handleZoomIn: () => void
  handleZoomOut: () => void
  handleResetZoom: () => void
  canPan: boolean
  handlePan: (direction: PanDirection) => () => void
  src?: string
  alt?: string
  className?: string
  tabIndex?: number
  isFullscreen?: boolean
  onFullscreenChange?: (status: boolean) => void
  fullscreenButtonRef?: React.RefObject<HTMLButtonElement>
  children?: string | JSX.Element | JSX.Element[]
  width?: string
  iconColor?: string
  style?: {
    float?: 'right' | 'left'
    margin?: string
  }
}

export const ZoomableImageContent = forwardRef<any, ZoomableImageContentProps>((props, ref) => {
  const {
    src,
    alt,
    onFullscreenChange,
    isFullscreen,
    fullscreenButtonRef,
    children,
    imageScale,
    handleZoomIn,
    handleZoomOut,
    handleResetZoom,
    canPan,
    handlePan,
    width,
    iconColor,
    ...imageProps
  } = props

  const zoom = Math.trunc(100 * imageScale)
  const [hovered, setHovered] = useState(false)
  const [imageHeight, setImageHeight] = useState(0)
  const wrapperRef = useRef(null)

  const { float, margin } = imageProps.style || {}
  /*
    If image is centered, float does not exist, so use margin to determine if value should
    be center (in prompt) but align to normally if in answer choice (qti int/old int)
  */
  const alignmentValue =
    float === 'right'
      ? 'right'
      : float === 'left'
      ? 'left'
      : margin === 'auto'
      ? 'center'
      : 'normal'

  useLayoutEffect(() => {
    if (imageScale !== 1) {
      announce(`${zoom}%`)
    }
  }, [imageScale, zoom])

  const handleImageHover = (hoverStatus: boolean) => {
    if (isFullscreen) {
      return
    }

    setHovered(hoverStatus)
  }

  useEffect(() => {
    const height = wrapperRef.current?.querySelector('img')?.height || 0
    setImageHeight(height)
  }, [wrapperRef])

  const imageWidthPercentage = imageProps?.className?.includes('w-')
    ? imageProps?.className
        .split(' ')
        .find(item => item.includes('w-'))
        ?.split('-')[1] || '100'
    : '100'

  const handleViewerKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.currentTarget !== event.target) return
    if (!canPan) return

    const direction = getPanDirectionFromKey(event.key)
    if (!direction) return

    handlePan(direction)()
    event.preventDefault()
    event.stopPropagation()
  }

  const handleImageMouseDown = (event: MouseEvent<HTMLElement>) => {
    if (event.button !== 0) return
    if (imageScale !== 1) return

    event.preventDefault()
    event.stopPropagation()
    handleZoomIn()
  }

  return (
    <StyledBox
      ref={wrapperRef}
      onMouseOver={() => handleImageHover(true)}
      onMouseLeave={() => handleImageHover(false)}
      onKeyDown={handleViewerKeyDown}
      role='region'
      aria-label={
        // a11y: label the region with the image's alt so an image answer reads
        // "Answer Choice {A-D}: {alt}", not the unhelpful "Zoomable image viewer"
        // (the region's label wins over the inner <img alt> in name computation).
        isFullscreen
          ? 'Fullscreen zoomable image viewer'
          : alt || 'Zoomable image viewer'
      }
      tabIndex={0}
      component={'div'}
      scale={imageScale}
    >
      <ZoomPanel
        hovered={hovered || isFullscreen}
        // Prevent click/keyboard events from bubbling to parent choice interactions,
        // which would otherwise select an answer when the user is just zooming.
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation()
          }
        }}
      >
        {isFullscreen && (
          <Box flexGrow={1}>
            <CloseModalButton
              iconColor={iconColor}
              onClick={() => onFullscreenChange(false)}
              size='large'
              aria-label='Close fullscreen image'
            >
              <StyledIcon icon={faXmark} />
            </CloseModalButton>
          </Box>
        )}
        <StyledIconButton
          iconColor={iconColor}
          aria-label='Zoom out'
          aria-disabled={imageScale === 1}
          onClick={imageScale === 1 ? undefined : handleZoomOut}
        >
          <StyledIcon icon={faMagnifyingGlassMinus} />
        </StyledIconButton>
        <StyledChip
          iconColor={iconColor}
          label={`${zoom}%`}
          tabIndex={0}
          onClick={e => {
            handleResetZoom()
            // Keep focus on the chip after reset so it doesn't fly off
            // when the ZoomPanel hides.
            requestAnimationFrame(() => {
              const target = e.currentTarget as HTMLElement
              target?.focus()
            })
          }}
          aria-label={`Zoom is at ${zoom}%${
            zoom !== 100 ? '. Reset to 100%' : ''
          }`}
        />
        <StyledIconButton
          iconColor={iconColor}
          aria-label='Zoom in'
          aria-disabled={imageScale === 2}
          onClick={imageScale === 2 ? undefined : handleZoomIn}
        >
          <StyledIcon icon={faMagnifyingGlassPlus} />
        </StyledIconButton>
        {canPan && (
          <PanControls role='group' aria-label='Image pan controls'>
            <StyledIconButton
              iconColor={iconColor}
              aria-label='Pan image up'
              onClick={handlePan('up')}
            >
              <StyledIcon icon={faArrowUp} />
            </StyledIconButton>
            <StyledIconButton
              iconColor={iconColor}
              aria-label='Pan image down'
              onClick={handlePan('down')}
            >
              <StyledIcon icon={faArrowDown} />
            </StyledIconButton>
            <StyledIconButton
              iconColor={iconColor}
              aria-label='Pan image left'
              onClick={handlePan('left')}
            >
              <StyledIcon icon={faArrowLeft} />
            </StyledIconButton>
            <StyledIconButton
              iconColor={iconColor}
              aria-label='Pan image right'
              onClick={handlePan('right')}
            >
              <StyledIcon icon={faArrowRight} />
            </StyledIconButton>
          </PanControls>
        )}
        {!isFullscreen && (
          <StyledIconButton
            ref={fullscreenButtonRef}
            iconColor={iconColor}
            aria-label='use fullscreen'
            onClick={() => onFullscreenChange(true)}
          >
            <StyledIcon icon={faMaximize} />
          </StyledIconButton>
        )}
      </ZoomPanel>
      <StyledImageWrapper imgWidth={`${imageWidthPercentage}%`} onMouseDown={handleImageMouseDown}>
        <TransformComponent
          wrapperStyle={{ width: '100%', ...(isFullscreen && { height: `max(75vh, ${imageHeight}px)` }) }}
          contentStyle={{ width: '100%', justifyContent: alignmentValue }}
        >
          {children ? (
            <StyledSVGContainer isFullscreen={isFullscreen}>{children}</StyledSVGContainer>
          ) : (
            <StyledImage
              isFullscreen={isFullscreen}
              src={src}
              // a11y: one owner for the image description. Out of fullscreen the wrapper
              // region publishes the alt as its name (see aria-label above), so this image
              // is hidden from the accessibility tree rather than publishing that same name
              // a second time. In fullscreen the region takes the generic viewer label
              // instead, so the image stays exposed and carries the description itself.
              // The alt attribute is kept either way: it is also what the browser paints
              // in the image box when the src fails to load.
              alt={alt}
              aria-hidden={isFullscreen ? undefined : true}
              ref={ref}
              {...imageProps}
            />
          )}
        </TransformComponent>
      </StyledImageWrapper>
    </StyledBox>
  )
})

const getPanDirectionFromKey = (key: string): PanDirection | null => {
  switch (key) {
    case 'ArrowUp':
      return 'up'
    case 'ArrowDown':
      return 'down'
    case 'ArrowLeft':
      return 'left'
    case 'ArrowRight':
      return 'right'
    default:
      return null
  }
}

const StyledImageWrapper = styled(Box)<{ imgWidth?: string }>(({ imgWidth }) => ({
  '& .react-transform-component img.lat': {
    width: `${imgWidth} !important`,
  },
}))

const StyledBox = styled(Box)<{ scale: number }>(({ theme, scale }) => ({
  userSelect: 'none',
  width: '100%',
  ':hover': {
    cursor: scale > 1 ? 'grab' : 'zoom-in',
  },
  ':active': {
    cursor: scale > 1 && 'grabbing',
  },
  ':focus-visible': {
    ...focusStyle(theme).shadow,
  },
  ':focus-within': {
    '*': {
      visibility: 'visible',
    },
  },
}))

const StyledIconButton = styled(IconButton)<{ iconColor?: string }>(
  ({ theme, iconColor }) => {
    const color = iconColor || theme.palette.blueGrey[500]
    return {
      color,
      '&:hover': {
        backgroundColor: alpha(color, 0.2),
      },
      // a11y: keyboard focus ring only (:focus-visible) via the theme two-tone
      // ring; on mouse :focus suppress the box-shadow ring so a click doesn't
      // leave a stuck ring (SC 2.4.7)
      '&:focus-visible': {
        backgroundColor: alpha(color, 0.3),
        ...focusStyle(theme).shadow,
      },
      '&:focus:not(:focus-visible)': {
        outline: 'none',
        boxShadow: 'none',
      },
      "&.Mui-disabled, &[aria-disabled='true']": {
        color: alpha(color, 0.4),
      },
    }
  }
)

const StyledChip = styled(Chip)<{ iconColor?: string }>(
  ({ theme, iconColor }) => {
    const color = iconColor || theme.palette.blueGrey[500]
    return {
      color,
      '&:hover': {
        backgroundColor: alpha(color, 0.2),
      },
      // a11y: keyboard focus ring only (:focus-visible) via the theme two-tone
      // ring; on mouse :focus suppress the box-shadow ring so a click doesn't
      // leave a stuck ring (SC 2.4.7)
      '&:focus-visible': {
        backgroundColor: alpha(color, 0.3),
        ...focusStyle(theme).shadow,
      },
      '&:focus:not(:focus-visible)': {
        outline: 'none',
        boxShadow: 'none',
      },
    }
  }
)

const CloseModalButton = styled(StyledIconButton)({
  alignSelf: 'flex-start',
})

const PanControls = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  // a11y: wrap the 4 pan arrows so they reflow instead of overflowing a narrow
  // cell when zoomed in (SC 1.4.10)
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(0.25),
  marginLeft: theme.spacing(0.25),
}))

const ZoomPanel = styled(Box)<{ hovered: boolean }>(({ theme, hovered }) => ({
  alignItems: 'center',
  visibility: hovered ? 'visible' : 'hidden',
  display: 'flex',
  // a11y: wrap so controls reflow to a second line instead of clipping off the
  // left at high zoom / narrow widths (SC 1.4.10)
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
  rowGap: theme.spacing(0.25),
  width: '100%',
  marginBottom: theme.spacing(0.25),
  paddingRight: theme.spacing(1),
}))

const StyledImage = styled('img')<{
  isFullscreen: boolean
  imageWidthInPx?: string
}>(({ theme, isFullscreen }) => ({
  height: 'auto',
  width: isFullscreen ? '70vw' : undefined,
  maxHeight: 'calc(100vh - 120px)',
  maxWidth: '1238px',
  // a11y: preserve aspect ratio so a wide passage/stem graphic isn't squished when
  // maxHeight caps the height at high zoom / short screens (SC 1.4.10)
  objectFit: 'contain',
  // a11y: below tablet the page scrolls as one column, so drop the 100vh cap — the image
  // then fits its box tightly (no letterbox) and grows with zoom instead of shrinking (SC 1.4.10)
  [theme.breakpoints.down('tablet')]: {
    maxHeight: 'none',
  },
}))

const StyledSVGContainer = styled('div')<{ isFullscreen: boolean }>(({ theme, isFullscreen }) => ({
  width: '100%',
  '& svg': {
    height: 'auto',
    width: isFullscreen ? '70vw' : undefined,
    maxHeight: 'calc(100vh - 120px)',
  },
}))

const StyledIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  fontSize: theme.spacing(4),
  width: theme.spacing(4),
  height: theme.spacing(4),
}))
